import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";

import { useAdminScope } from "../../context/AdminScopeContext";

import {
  AdminEmptyState,
  AdminErrorState,
  AdminLoadingState,
} from "../../pages/admin/ui_engine";

import {
  AdminButton,
} from "../../pages/admin/action_engine";

import {
  AdminFilterSelect,
  AdminFormToolbar,
  AdminPagination,
  AdminSearchInput,
  AdminSection,
  AdminSectionHeader,
  AdminSectionTitle,
  AdminTable,
  AdminTableViewport,
} from "../../pages/admin/section_engine";

import {
  DEFAULT_HR_EMPLOYEE_PAGE_SIZE,
  HR_CONTRACT_TYPES,
  HR_EMPLOYEE_STATUSES,
  HR_EMPLOYMENT_STATES,
  listEmployees,
} from "../../services/hrAdminApi";


export const HR_EMPLOYEE_DIRECTORY_STATUS =
  Object.freeze({
    LOADING: "loading",
    EMPTY: "empty",
    ERROR: "error",
    READY: "ready",
  });


const EMPTY_PAGINATION = Object.freeze({
  page: 1,
  page_size: DEFAULT_HR_EMPLOYEE_PAGE_SIZE,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_previous: false,
});


function normalizeDirectoryPayload(payload) {
  const pagination = (
    payload?.pagination
    && typeof payload.pagination === "object"
  )
    ? payload.pagination
    : {};

  const page = Number(
    pagination.page
    ?? payload?.page
    ?? 1,
  );

  const pageSize = Number(
    pagination.page_size
    ?? payload?.page_size
    ?? DEFAULT_HR_EMPLOYEE_PAGE_SIZE,
  );

  const total = Number(
    pagination.total
    ?? payload?.total
    ?? 0,
  );

  const totalPages = Number(
    pagination.total_pages
    ?? payload?.total_pages
    ?? 0,
  );

  return {
    items: Array.isArray(payload?.items)
      ? payload.items
      : [],
    pagination: {
      page:
        Number.isInteger(page) && page > 0
          ? page
          : 1,
      page_size:
        Number.isInteger(pageSize) && pageSize > 0
          ? pageSize
          : DEFAULT_HR_EMPLOYEE_PAGE_SIZE,
      total:
        Number.isFinite(total) && total >= 0
          ? total
          : 0,
      total_pages:
        Number.isInteger(totalPages)
        && totalPages >= 0
          ? totalPages
          : 0,
      has_next:
        typeof pagination.has_next === "boolean"
          ? pagination.has_next
          : (
            Number.isInteger(totalPages)
            && totalPages > 0
            && page < totalPages
          ),
      has_previous:
        typeof pagination.has_previous === "boolean"
          ? pagination.has_previous
          : page > 1,
    },
  };
}


function resolveErrorMessage(error) {
  return (
    error?.response?.data?.message
    || error?.response?.data?.error_description
    || error?.response?.data?.error
    || error?.message
    || "Impossible de charger l’annuaire salariés."
  );
}


function displayValue(value) {
  if (
    value === null
    || value === undefined
    || value === ""
  ) {
    return "—";
  }

  return String(value);
}


function resolveEmployeeStatus(employee) {
  return (
    employee.employee_status
    ?? employee.status
    ?? null
  );
}


function resolveEmploymentStart(employee) {
  return (
    employee.employment_start_date
    ?? employee.start_date
    ?? employee.hire_date
    ?? null
  );
}


function resolveUserLinked(employee) {
  if (typeof employee.user_linked === "boolean") {
    return employee.user_linked;
  }

  return Boolean(
    employee.user_uuid
    ?? employee.linked_user_uuid,
  );
}


function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return displayValue(value);
  }

  return new Intl.DateTimeFormat(
    "fr-FR",
  ).format(date);
}


export default function HREmployeeDirectoryAdminPanel() {
  const navigate = useNavigate();

  const {
    effectiveCompanyUuid,
  } = useAdminScope();

  const [status, setStatus] = useState(
    HR_EMPLOYEE_DIRECTORY_STATUS.LOADING,
  );

  const [employees, setEmployees] = useState([]);
  const [pagination, setPagination] = useState(
    EMPTY_PAGINATION,
  );

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [employeeStatus, setEmployeeStatus] =
    useState("");

  const [contractType, setContractType] =
    useState("");

  const [employmentState, setEmploymentState] =
    useState("");

  const [userLinkedFilter, setUserLinkedFilter] =
    useState("");

  const [errorMessage, setErrorMessage] =
    useState("");


  const resolvedUserLinked = useMemo(() => {
    if (userLinkedFilter === "linked") {
      return true;
    }

    if (userLinkedFilter === "unlinked") {
      return false;
    }

    return undefined;
  }, [userLinkedFilter]);


  const loadEmployees = useCallback(
    async ({
      requestedPage = 1,
      requestedSearch = search,
      requestedEmployeeStatus = employeeStatus,
      requestedContractType = contractType,
      requestedEmploymentState = employmentState,
      requestedUserLinked = resolvedUserLinked,
    } = {}) => {
      setStatus(
        HR_EMPLOYEE_DIRECTORY_STATUS.LOADING,
      );
      setErrorMessage("");

      try {
        const payload = await listEmployees({
          companyUuid: effectiveCompanyUuid,
          search: requestedSearch,
          employeeStatus:
            requestedEmployeeStatus,
          contractType:
            requestedContractType,
          employmentState:
            requestedEmploymentState,
          userLinked:
            requestedUserLinked,
          page: requestedPage,
          pageSize:
            DEFAULT_HR_EMPLOYEE_PAGE_SIZE,
        });

        const normalized =
          normalizeDirectoryPayload(payload);

        setEmployees(normalized.items);
        setPagination(normalized.pagination);

        setStatus(
          normalized.items.length
            ? HR_EMPLOYEE_DIRECTORY_STATUS.READY
            : HR_EMPLOYEE_DIRECTORY_STATUS.EMPTY,
        );
      } catch (error) {
        setEmployees([]);
        setPagination(EMPTY_PAGINATION);
        setErrorMessage(
          resolveErrorMessage(error),
        );
        setStatus(
          HR_EMPLOYEE_DIRECTORY_STATUS.ERROR,
        );
      }
    },
    [
      contractType,
      effectiveCompanyUuid,
      employeeStatus,
      employmentState,
      resolvedUserLinked,
      search,
    ],
  );


  useEffect(() => {
    loadEmployees({
      requestedPage: 1,
    });
  }, [
    loadEmployees,
  ]);


  const handleSearchSubmit = (event) => {
    event.preventDefault();

    const normalizedSearch =
      searchInput.trim();

    if (normalizedSearch === search) {
      loadEmployees({
        requestedPage: 1,
        requestedSearch: normalizedSearch,
      });

      return;
    }

    setSearch(normalizedSearch);
  };


  const handleReset = () => {
    setSearchInput("");
    setSearch("");
    setEmployeeStatus("");
    setContractType("");
    setEmploymentState("");
    setUserLinkedFilter("");
  };


  const handleFilterChange = (
    setter,
    value,
  ) => {
    setter(value);
  };


  const hasActiveFilters = Boolean(
    search
    || searchInput
    || employeeStatus
    || contractType
    || employmentState
    || userLinkedFilter,
  );


  const openEmployeeWorkspace = (employee) => {
    const employeeUuid =
      typeof employee?.employee_uuid === "string"
        ? employee.employee_uuid.trim()
        : "";

    if (!employeeUuid) {
      return;
    }

    navigate(
      `/admin/hr/employees/${encodeURIComponent(
        employeeUuid,
      )}`,
    );
  };


  return (
    <DirectoryRoot>
      <AdminSection>
        <AdminSectionHeader>
          <AdminSectionTitle>
            <div>
              <h4>Employee Directory</h4>
              <p>
                Référentiel administratif des salariés
                du scope société actif.
              </p>
            </div>
          </AdminSectionTitle>
        </AdminSectionHeader>

        <AdminFormToolbar
          onSubmit={handleSearchSubmit}
        >
          <AdminSearchInput
            value={searchInput}
            onChange={(event) => {
              setSearchInput(event.target.value);
            }}
            placeholder={
              "Nom, prénom, matricule, email ou poste"
            }
            aria-label="Rechercher un salarié"
          />

          <AdminFilterSelect
            value={employeeStatus}
            aria-label="Filtrer par statut salarié"
            onChange={(event) => {
              handleFilterChange(
                setEmployeeStatus,
                event.target.value,
              );
            }}
          >
            <option value="">
              Tous les statuts
            </option>

            {HR_EMPLOYEE_STATUSES.map(
              (value) => (
                <option
                  key={value}
                  value={value}
                >
                  {value}
                </option>
              ),
            )}
          </AdminFilterSelect>

          <AdminFilterSelect
            value={contractType}
            aria-label="Filtrer par type de contrat"
            onChange={(event) => {
              handleFilterChange(
                setContractType,
                event.target.value,
              );
            }}
          >
            <option value="">
              Tous les contrats
            </option>

            {HR_CONTRACT_TYPES.map(
              (value) => (
                <option
                  key={value}
                  value={value}
                >
                  {value}
                </option>
              ),
            )}
          </AdminFilterSelect>

          <AdminFilterSelect
            value={employmentState}
            aria-label="Filtrer par état d’emploi"
            onChange={(event) => {
              handleFilterChange(
                setEmploymentState,
                event.target.value,
              );
            }}
          >
            <option value="">
              Tous les états d’emploi
            </option>

            {HR_EMPLOYMENT_STATES.map(
              (value) => (
                <option
                  key={value}
                  value={value}
                >
                  {value}
                </option>
              ),
            )}
          </AdminFilterSelect>

          <AdminFilterSelect
            value={userLinkedFilter}
            aria-label="Filtrer par compte utilisateur"
            onChange={(event) => {
              setUserLinkedFilter(
                event.target.value,
              );
            }}
          >
            <option value="">
              Tous les comptes
            </option>
            <option value="linked">
              User associé
            </option>
            <option value="unlinked">
              Aucun User associé
            </option>
          </AdminFilterSelect>

          <AdminButton
            type="submit"
            variant="primary"
          >
            Rechercher
          </AdminButton>

          {hasActiveFilters && (
            <AdminButton
              type="button"
              variant="secondary"
              onClick={handleReset}
            >
              Réinitialiser
            </AdminButton>
          )}
        </AdminFormToolbar>

        {status
          === HR_EMPLOYEE_DIRECTORY_STATUS.LOADING
          && (
            <AdminLoadingState
              message="Chargement des salariés..."
            />
          )}

        {status
          === HR_EMPLOYEE_DIRECTORY_STATUS.ERROR
          && (
            <AdminErrorState
              message={errorMessage}
              compact
            />
          )}

        {status
          === HR_EMPLOYEE_DIRECTORY_STATUS.EMPTY
          && (
            <AdminEmptyState
              message={
                hasActiveFilters
                  ? "Aucun salarié ne correspond aux critères."
                  : "Aucun salarié n’est disponible dans ce scope."
              }
            />
          )}

        {status
          === HR_EMPLOYEE_DIRECTORY_STATUS.READY
          && (
            <>
              <DirectorySummary
                aria-live="polite"
              >
                <strong>
                  {pagination.total}
                </strong>{" "}
                salarié
                {pagination.total > 1
                  ? "s"
                  : ""}
              </DirectorySummary>

              <AdminTableViewport>
                <DirectoryTable>
                  <thead>
                    <tr>
                      <th scope="col">
                        Matricule
                      </th>
                      <th scope="col">
                        Nom
                      </th>
                      <th scope="col">
                        Prénom
                      </th>
                      <th scope="col">
                        Entreprise
                      </th>
                      <th scope="col">
                        Établissement
                      </th>
                      <th scope="col">
                        Service
                      </th>
                      <th scope="col">
                        Poste
                      </th>
                      <th scope="col">
                        Manager
                      </th>
                      <th scope="col">
                        Statut
                      </th>
                      <th scope="col">
                        Date d’entrée
                      </th>
                      <th scope="col">
                        Compte utilisateur
                      </th>
                      <th scope="col">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {employees.map(
                      (employee) => (
                        <tr
                          key={employee.employee_uuid}
                        >
                          <td>
                            {displayValue(
                              employee.employee_number,
                            )}
                          </td>

                          <td>
                            {displayValue(
                              employee.last_name,
                            )}
                          </td>

                          <td>
                            {displayValue(
                              employee.first_name,
                            )}
                          </td>

                          <td>
                            {displayValue(
                              employee.company_name,
                            )}
                          </td>

                          <td>
                            {displayValue(
                              employee.establishment_name,
                            )}
                          </td>

                          <td>
                            {displayValue(
                              employee.department_name
                              ?? employee.service_name,
                            )}
                          </td>

                          <td>
                            {displayValue(
                              employee.position_name,
                            )}
                          </td>

                          <td>
                            {displayValue(
                              employee.manager_name,
                            )}
                          </td>

                          <td>
                            {displayValue(
                              resolveEmployeeStatus(
                                employee,
                              ),
                            )}
                          </td>

                          <td>
                            {formatDate(
                              resolveEmploymentStart(
                                employee,
                              ),
                            )}
                          </td>

                          <td>
                            {resolveUserLinked(
                              employee,
                            )
                              ? "Associé"
                              : "Non associé"}
                          </td>

                          <td>
                            <AdminButton
                              type="button"
                              variant="secondary"
                              aria-label={`Ouvrir la fiche salarié ${
                                employee.employee_number
                                || employee.employee_uuid
                                || ""
                              }`}
                              onClick={() => {
                                openEmployeeWorkspace(
                                  employee,
                                );
                              }}
                            >
                              Ouvrir
                            </AdminButton>
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </DirectoryTable>
              </AdminTableViewport>

              <AdminPagination
                ariaLabel={
                  "Pagination de l’annuaire salariés"
                }
                previousDisabled={
                  !pagination.has_previous
                }
                nextDisabled={
                  !pagination.has_next
                }
                onPrevious={() => {
                  loadEmployees({
                    requestedPage: Math.max(
                      1,
                      pagination.page - 1,
                    ),
                  });
                }}
                onNext={() => {
                  loadEmployees({
                    requestedPage:
                      pagination.page + 1,
                  });
                }}
                label={`Page ${pagination.page} / ${Math.max(
                  pagination.total_pages,
                  1,
                )}`}
              />
            </>
          )}
      </AdminSection>
    </DirectoryRoot>
  );
}


const DirectoryRoot = styled.div`
  min-width: 0;
`;


const DirectorySummary = styled.p`
  margin: 0.85rem 0;
  color: rgba(247, 247, 247, 0.76);
`;


const DirectoryTable = styled(AdminTable)`
  min-width: 1320px;

  td {
    vertical-align: top;
  }
`;
